import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { SettingService } from '../../../../../services/setting.service';
import { IntegrationService } from '../../../../../services/integration.service';
import { ToastrService } from 'ngx-toastr';
import { FNFResponse } from '../../../../../models/response';

@Component({
  selector: 'app-uber-eats-setup',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    MatIcon,
    CommonModule,
    NgxSpinnerModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    MatIcon,
  ],
  templateUrl: './uber-eats-setup.component.html',
  styleUrl: './uber-eats-setup.component.css',
})
export class UberEatsSetupComponent implements OnInit {
  private readonly fb = inject(FormBuilder);

  settingService = inject(SettingService);
  integrationService = inject(IntegrationService);
  ngxSpinnerService = inject(NgxSpinnerService);
  toastr = inject(ToastrService);
  dialog = inject(MatDialog);
  currentRestaurantSettings: any;
  uberSettings: any;
  orderAutoApprove: boolean = false;
  availability: boolean = false;
  disableAvailabilityContent: boolean = false;
  preparationTime: number = 0;
  preparationTimeErrorMessage: string = '';

  readonly minResumeDate = this.getTodayDateString();
  readonly offlineReason = 'PAUSED_BY_RESTAURANT';
  readonly offlineReasonLabel = 'Paused by restaurant';

  availabilityForm = this.fb.group(
    {
      resumeDate: [this.getTodayDateString(), Validators.required],
      resumeTime: [this.getDefaultTimeString(), Validators.required],
    },
    { validators: [this.resumeDateTimeNotInPastValidator.bind(this)] },
  );

  constructor() {}

  ngOnInit() {
    this.availabilityForm.get('resumeDate')?.valueChanges.subscribe(() => {
      this.availabilityForm.get('resumeTime')?.updateValueAndValidity();
      this.availabilityForm.updateValueAndValidity();
    });
    this.availabilityForm.get('resumeTime')?.valueChanges.subscribe(() => {
      this.availabilityForm.updateValueAndValidity();
    });

    this.settingService.currentRestaurantSettings$.subscribe((settings: any) => {
      this.currentRestaurantSettings = settings;
      this.uberSettings = this.currentRestaurantSettings?.find(
        (type: any) => type.setting_type === 'delivery_uber',
      )?.settings_config;
      this.orderAutoApprove = this.uberSettings?.auto_approve?.enabled;
      this.preparationTime = this.uberSettings?.preparation_time || 0;
    });
    this.getUberStoreStatus();
  }

  preventNegativeInput(event: any) {
    if (event.key === '-') {
      event.preventDefault();
    }
  }

  toggleOrderAutoApprove(event: any) {
    // Store the previous value before making the API call
    const previousValue = this.orderAutoApprove;
    const newValue = event.target.checked;

    // Temporarily update the UI to show the new value
    this.orderAutoApprove = newValue;

    let payload = {
      delivery_type: 'uber',
      setting_key: 'auto_approve',
      value: newValue,
    };
    this.ngxSpinnerService.show();
    this.integrationService.changeUberEatsAutoApprove(payload).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.orderAutoApprove = previousValue; // Revert to previous value
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.orderAutoApprove = previousValue; // Revert to previous value
          this.ngxSpinnerService.hide();
          return;
        }
        this.toastr.success('Uber Eats auto approve updated', 'Success');
        // Keep the new value since API succeeded
        // after success, update the current restaurant settings
        this.uberSettings.auto_approve.enabled = newValue;
        this.settingService.currentRestaurantSettings$.next(this.currentRestaurantSettings);
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.orderAutoApprove = previousValue; // Revert to previous value on error
        this.ngxSpinnerService.hide();
      },
    });
  }

  changePreparationTime(event: any) {
    this.preparationTime = event.target.value;
    if (this.preparationTime < 5 || this.preparationTime > 180) {
      this.preparationTimeErrorMessage = 'Preparation time must be between 5 and 180 minutes.';
    } else {
      this.preparationTimeErrorMessage = '';
    }
  }

  savePreparationTime() {
    let payload = {
      delivery_type: 'uber',
      value: this.preparationTime,
    };
    this.ngxSpinnerService.show();
    this.integrationService.changeUberEatsPreparationTime(payload).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else {
          this.toastr.success('Uber Eats preparation time updated', 'Success');
          // after success, update the current restaurant settings
          this.uberSettings.preparation_time = this.preparationTime;
          this.settingService.currentRestaurantSettings$.next(this.currentRestaurantSettings);
          this.ngxSpinnerService.hide();
        }
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  onCloseModal() {
    this.dialog.closeAll();
  }

  toggleAvailability(event: any) {
    this.availability = event.target.checked;
    if (event.target.checked) {
      this.disableAvailabilityContent = false;
      let payload = {
        status: 'online',
      };
      this.changeUberStoreStatus(payload);
    } else {
      this.disableAvailabilityContent = true;
    }
  }

  get minResumeTime(): string | null {
    const date = this.availabilityForm.get('resumeDate')?.value;
    if (!date || date !== this.minResumeDate) {
      return null;
    }
    const now = new Date();
    return `${String(now.getUTCHours()).padStart(2, '0')}:${String(now.getUTCMinutes()).padStart(2, '0')}`;
  }

  saveAvailability() {
    if (!this.disableAvailabilityContent) {
      return;
    }

    if (this.availabilityForm.invalid) {
      this.availabilityForm.markAllAsTouched();
      return;
    }

    const { resumeDate, resumeTime } = this.availabilityForm.getRawValue();
    if (!resumeDate || !resumeTime) {
      return;
    }

    const payload = {
      status: 'offline',
      reason: this.offlineReason,
      is_offline_until: this.formatUtcIsoDateTime(resumeDate, resumeTime),
    };
    this.changeUberStoreStatus(payload);
  }

  private getTodayDateString(): string {
    const d = new Date();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${d.getUTCFullYear()}-${month}-${day}`;
  }

  private getDefaultTimeString(): string {
    const d = new Date();
    d.setUTCHours(d.getUTCHours() + 0);
    const hours = String(d.getUTCHours()).padStart(2, '0');
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  /** Treat form date/time as UTC wall-clock and return epoch ms. */
  private parseResumeDateTimeUtc(date: string, time: string): number {
    const [year, month, day] = date.split('-').map(Number);
    const [hours, minutes] = time.split(':').map(Number);
    if ([year, month, day, hours, minutes].some((n) => Number.isNaN(n))) {
      return Number.NaN;
    }
    return Date.UTC(year, month - 1, day, hours, minutes, 0);
  }

  /** Parse API UTC ISO into form date (YYYY-MM-DD) and time (HH:mm). */
  private utcIsoToFormDateAndTime(iso: string): { date: string; time: string } | null {
    const parsed = Date.parse(iso);
    if (Number.isNaN(parsed)) {
      return null;
    }
    const d = new Date(parsed);
    const pad = (n: number) => String(n).padStart(2, '0');
    return {
      date: `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`,
      time: `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`,
    };
  }

  private patchAvailabilityFormFromOfflineStatus(storeData: any): void {
    if (!storeData) {
      return;
    }

    const offlineUntil =
      storeData.is_offline_until ??
      storeData.offline_until ??
      storeData.resume_at ??
      storeData.resume_orders_at;

    const patch: { resumeDate?: string; resumeTime?: string } = {};

    if (offlineUntil) {
      const formDateTime = this.utcIsoToFormDateAndTime(String(offlineUntil));
      if (formDateTime) {
        patch.resumeDate = formDateTime.date;
        patch.resumeTime = formDateTime.time;
      }
    }

    if (Object.keys(patch).length > 0) {
      this.availabilityForm.patchValue(patch);
      this.availabilityForm.updateValueAndValidity();
    }
  }

  /** e.g. 2026-06-21T17:08:30+00:00 */
  private formatUtcIsoDateTime(date: string, time: string): string {
    const utcMs = this.parseResumeDateTimeUtc(date, time);
    const d = new Date(utcMs);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}+00:00`;
  }

  private resumeDateTimeNotInPastValidator(group: AbstractControl): ValidationErrors | null {
    const date = group.get('resumeDate')?.value;
    const time = group.get('resumeTime')?.value;
    if (!date || !time) {
      return null;
    }

    const selectedUtc = this.parseResumeDateTimeUtc(date, time);
    if (Number.isNaN(selectedUtc)) {
      return { invalidDateTime: true };
    }

    if (selectedUtc <= Date.now()) {
      return { pastDateTime: true };
    }

    return null;
  }

  getUberStoreStatus() {
    this.ngxSpinnerService.show();
    this.integrationService.getUberStoreStatus().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else {
          const storeData = response.data?.data ?? response.data;
          const status = (storeData?.status ?? '').toString().toLowerCase();
          this.availability = status === 'online';
          if (this.availability) {
            this.disableAvailabilityContent = false;
          } else {
            this.disableAvailabilityContent = true;
            this.patchAvailabilityFormFromOfflineStatus(storeData);
          }
          this.ngxSpinnerService.hide();
        }
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  changeUberStoreStatus(payload: any) {
    this.ngxSpinnerService.show();
    this.integrationService.changeUberStoreStatus(payload).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else {
          this.toastr.success('Uber Eats store status updated', 'Success');
          this.ngxSpinnerService.hide();
          const storeData = response.data?.data ?? response.data;
          const status = (storeData?.status ?? '').toString().toLowerCase();
          this.availability = status.toLowerCase() === 'online';
          if (!this.availability) {
            this.disableAvailabilityContent = true;
            this.patchAvailabilityFormFromOfflineStatus(storeData);
          } else {
            this.disableAvailabilityContent = false;
          }
        }
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }
}
